import {
  AXIS_HEIGHT,
  BAR_HEIGHT,
  GANTT_STATUS_COLORS,
  GANTT_STATUS_LABEL,
  MAX_CONTAINER_HEIGHT,
  MAX_PX_PER_DAY,
  MIN_CONTAINER_HEIGHT,
  MIN_PX_PER_DAY,
} from "./gantt-constants";
import { diffDays, formatDateLabel, maxDate, minDate } from "./gantt-date-utils";
import type { GanttTooltipContent, GanttTooltipProps } from "./GanttTooltip";
import type {
  GanttLayout,
  GanttTask,
  GanttTaskLayout,
  ParsedGantt,
} from "./gantt-types";

const DEFAULT_RANGE_DAYS = 1;
const MAX_COMPACT_PREVIEW_TASKS = 6;
const MIN_COMPACT_TASK_WIDTH_PERCENT = 3;
const MIN_TOOLTIP_VIEWPORT_SIZE = 0;
const ZOOM_EPSILON = 0.001;

export interface ScrollHintState {
  showLeft: boolean;
  showRight: boolean;
}

export interface TimelineViewportState {
  scrollLeft: number;
  scrollTop: number;
  width: number;
  height: number;
}

export interface GanttDependencyLineLayout {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
}

export interface GanttTooltipAnchorData {
  x: number;
  y: number;
  content: GanttTooltipContent;
}

export interface GanttZoomButtonState {
  isAtMaxZoom: boolean;
  isAtMinZoom: boolean;
}

export interface GanttTaskInteractionState {
  isHovered: boolean;
  isDimmed: boolean;
}

export interface GanttCompactPreviewTaskModel {
  id: string;
  startPercent: number;
  widthPercent: number;
  color: string;
}

export interface GanttCompactPreviewModel {
  tasks: GanttCompactPreviewTaskModel[];
}

interface SelectRangeDaysForZoomSelectionParams {
  inputTasks: Pick<GanttTask, "startDate" | "endDate">[];
}

interface SelectGanttContainerHeightParams {
  chartHeight: number;
}

interface SelectZoomButtonStateParams {
  effectivePxPerDay: number;
}

interface SelectTaskDurationDaysParams {
  taskLayout: GanttTaskLayout;
}

interface SelectActiveHoveredTaskLayoutParams {
  layout: GanttLayout | null;
  hoveredTaskId: string | null;
}

interface SelectHighlightedTaskIdsParams {
  activeHoveredTaskLayout: GanttTaskLayout | null;
}

interface SelectDependencyLineLayoutsParams {
  layout: GanttLayout | null;
  activeHoveredTaskLayout: GanttTaskLayout | null;
}

interface SelectTooltipDataParams {
  activeHoveredTaskLayout: GanttTaskLayout | null;
}

interface SelectTooltipPlacementDataParams {
  tooltipData: GanttTooltipAnchorData | null;
  timelineViewportState: TimelineViewportState;
}

interface SelectTaskInteractionStateParams {
  taskId: string;
  hoveredTaskId: string | null;
  highlightedTaskIds: ReadonlySet<string>;
}

interface SelectCompactPreviewModelParams {
  parsed: ParsedGantt | null;
}

export function selectRangeDaysForZoomSelection({
  inputTasks,
}: SelectRangeDaysForZoomSelectionParams): number {
  const firstTask = inputTasks[0];
  if (!firstTask) {
    return DEFAULT_RANGE_DAYS;
  }

  const rangeStart = inputTasks.reduce((minDateCandidate, task) => {
    return minDate(minDateCandidate, task.startDate);
  }, firstTask.startDate);
  const rangeEnd = inputTasks.reduce((maxDateCandidate, task) => {
    return maxDate(maxDateCandidate, task.endDate);
  }, firstTask.endDate);

  return Math.max(DEFAULT_RANGE_DAYS, diffDays(rangeStart, rangeEnd));
}

export function selectGanttContainerHeight({
  chartHeight,
}: SelectGanttContainerHeightParams): number {
  return Math.max(
    MIN_CONTAINER_HEIGHT,
    Math.min(MAX_CONTAINER_HEIGHT, chartHeight + 20),
  );
}

export function selectZoomButtonState({
  effectivePxPerDay,
}: SelectZoomButtonStateParams): GanttZoomButtonState {
  return {
    isAtMaxZoom: effectivePxPerDay >= MAX_PX_PER_DAY - ZOOM_EPSILON,
    isAtMinZoom: effectivePxPerDay <= MIN_PX_PER_DAY + ZOOM_EPSILON,
  };
}

export function selectTaskDurationDays({
  taskLayout,
}: SelectTaskDurationDaysParams): number {
  return Math.max(0, diffDays(taskLayout.task.startDate, taskLayout.task.endDate));
}

export function selectActiveHoveredTaskLayout({
  layout,
  hoveredTaskId,
}: SelectActiveHoveredTaskLayoutParams): GanttTaskLayout | null {
  if (!layout || !hoveredTaskId) {
    return null;
  }

  return layout.taskLayoutById.get(hoveredTaskId) ?? null;
}

export function selectHighlightedTaskIds({
  activeHoveredTaskLayout,
}: SelectHighlightedTaskIdsParams): Set<string> {
  if (!activeHoveredTaskLayout) {
    return new Set<string>();
  }

  return new Set<string>([
    activeHoveredTaskLayout.task.id,
    ...activeHoveredTaskLayout.task.dependsOn,
  ]);
}

export function selectDependencyLineLayouts({
  layout,
  activeHoveredTaskLayout,
}: SelectDependencyLineLayoutsParams): GanttDependencyLineLayout[] {
  if (!layout || !activeHoveredTaskLayout) {
    return [];
  }

  return activeHoveredTaskLayout.task.dependsOn
    .map((dependencyTaskId) => {
      const dependencyLayout = layout.taskLayoutById.get(dependencyTaskId);
      if (!dependencyLayout) {
        return null;
      }

      return {
        id: `${dependencyTaskId}-${activeHoveredTaskLayout.task.id}`,
        x1: dependencyLayout.x + dependencyLayout.width,
        y1: dependencyLayout.centerY,
        x2: activeHoveredTaskLayout.x,
        y2: activeHoveredTaskLayout.centerY,
        color: GANTT_STATUS_COLORS[dependencyLayout.task.status],
      };
    })
    .filter((line): line is GanttDependencyLineLayout => line != null);
}

export function selectTooltipData({
  activeHoveredTaskLayout,
}: SelectTooltipDataParams): GanttTooltipAnchorData | null {
  if (!activeHoveredTaskLayout) {
    return null;
  }

  return {
    x: activeHoveredTaskLayout.x + activeHoveredTaskLayout.width / 2,
    y: activeHoveredTaskLayout.y + BAR_HEIGHT / 2,
    content: {
      taskLabel: activeHoveredTaskLayout.task.label,
      startLabel: formatDateLabel(activeHoveredTaskLayout.task.startDate),
      endLabel: formatDateLabel(activeHoveredTaskLayout.task.endDate),
      durationDays: selectTaskDurationDays({
        taskLayout: activeHoveredTaskLayout,
      }),
      status: activeHoveredTaskLayout.task.status,
      statusLabel: GANTT_STATUS_LABEL[activeHoveredTaskLayout.task.status],
      statusColor: GANTT_STATUS_COLORS[activeHoveredTaskLayout.task.status],
    },
  };
}

export function selectTooltipPlacementData({
  tooltipData,
  timelineViewportState,
}: SelectTooltipPlacementDataParams): GanttTooltipProps["tooltip"] {
  if (
    !tooltipData ||
    timelineViewportState.width <= MIN_TOOLTIP_VIEWPORT_SIZE ||
    timelineViewportState.height <= MIN_TOOLTIP_VIEWPORT_SIZE
  ) {
    return null;
  }

  return {
    anchorX: tooltipData.x,
    anchorY: tooltipData.y,
    scrollLeft: timelineViewportState.scrollLeft,
    scrollTop: timelineViewportState.scrollTop,
    containerWidth: timelineViewportState.width,
    containerHeight: timelineViewportState.height,
    content: tooltipData.content,
  };
}

export function selectTaskInteractionState({
  taskId,
  hoveredTaskId,
  highlightedTaskIds,
}: SelectTaskInteractionStateParams): GanttTaskInteractionState {
  const isHovered = hoveredTaskId === taskId;

  return {
    isHovered,
    isDimmed: !!hoveredTaskId && !highlightedTaskIds.has(taskId),
  };
}

export function selectCompactPreviewModel({
  parsed,
}: SelectCompactPreviewModelParams): GanttCompactPreviewModel | null {
  if (!parsed || parsed.tasks.length < 1) {
    return null;
  }

  const tasks = parsed.tasks.slice(0, MAX_COMPACT_PREVIEW_TASKS);
  const firstTask = tasks[0];
  if (!firstTask) {
    return null;
  }

  const rangeStart = tasks.reduce(
    (minDateCandidate, task) => minDate(minDateCandidate, task.startDate),
    firstTask.startDate,
  );
  const rangeEnd = tasks.reduce(
    (maxDateCandidate, task) => maxDate(maxDateCandidate, task.endDate),
    firstTask.endDate,
  );
  const totalMs = Math.max(1, rangeEnd.getTime() - rangeStart.getTime());

  return {
    tasks: tasks.map((task) => ({
      id: task.id,
      startPercent:
        ((task.startDate.getTime() - rangeStart.getTime()) / totalMs) * 100,
      widthPercent: Math.max(
        MIN_COMPACT_TASK_WIDTH_PERCENT,
        ((task.endDate.getTime() - task.startDate.getTime()) / totalMs) * 100,
      ),
      color: GANTT_STATUS_COLORS[task.status],
    })),
  };
}

export function selectTimelineRowsHeight({
  chartHeight,
}: SelectGanttContainerHeightParams): number {
  return chartHeight - AXIS_HEIGHT;
}
